import React from "react";
import {RightsMapper} from "./RightsMapper.ts";
import {type Permission} from "./RightsSystem.ts";
import {useAuth} from "react-oidc-context";
import {devWarn} from "../../utils/dev.ts";

export const useIsAuthorized = (permission: Permission): boolean => {
    const auth = useAuth();
    if (!permission) {
        if (import.meta.env.DEV) devWarn("useIsAuthorized: no permission given");
        return false;
    }
    if (!RightsMapper.hasMapping(permission)) {
        if (import.meta.env.DEV) devWarn(`useIsAuthorized: no mapping for "${permission}"`);
        return false;
    }
    const mappedAuth = RightsMapper.getMapping(permission);
    const authorities = (auth.user?.profile.authorities as string[] | undefined) ?? [];
    return RightsMapper.isSuperAdmin(authorities) || authorities.includes(mappedAuth);
};

interface PermissionExtras {
    permission: Permission;
    permittedOnClick: () => void;
    permittedClassList?: string;
}

export function withPermission<P extends {onClick?: () => void; className?: string}>(
    Wrapped: React.ComponentType<P>,
): React.FC<Omit<P, "onClick"> & PermissionExtras> {
    const Wrapper: React.FC<Omit<P, "onClick"> & PermissionExtras> = ({
        permission,
        permittedOnClick,
        permittedClassList,
        ...rest
    }) => {
        const isAuthorized = useIsAuthorized(permission);
        const baseClassName = (rest as {className?: string}).className ?? "";
        const className = isAuthorized && permittedClassList
            ? `${baseClassName} ${permittedClassList}`.trim()
            : baseClassName;
        // HOC generics: TS can't prove that Omit<P, "onClick"> + a new onClick
        // re-satisfies P (P could be a subtype with a different onClick shape),
        // so we cast through `unknown` at the spread boundary.
        return (
            <Wrapped
                {...(rest as unknown as P)}
                onClick={isAuthorized ? permittedOnClick : undefined}
                className={className}
            />
        );
    };
    Wrapper.displayName = `withPermission(${Wrapped.displayName ?? Wrapped.name ?? "Component"})`;
    return Wrapper;
}
